import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import axiosConfig from '../../../../../config/axiosConfig';
import './PaymentManagement.css';

// ─── Icons ───
const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconRefresh = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const IconChevLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const IconChevRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const IconArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const IconExternalLink = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

// ─── Helpers ───
const fmt = (amount) => {
  const n = parseFloat(amount) || 0;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const initials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

const statusCls = (s = '') => {
  const v = s.toLowerCase();
  if (['captured', 'success', 'completed', 'credit', 'active'].includes(v)) return 'pm-pill green';
  if (['failed', 'declined', 'debit', 'inactive'].includes(v)) return 'pm-pill red';
  if (['pending', 'processing'].includes(v)) return 'pm-pill amber';
  return 'pm-pill grey';
};

// ─── Pagination Bar ───
const PaginationBar = ({ pagination, onPageChange }) => {
  if (!pagination || pagination.totalPages <= 1) return null;
  const { page, totalPages, total, pageSize } = pagination;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const maxVisible = 5;
  let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
  let endPage = startPage + maxVisible - 1;
  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxVisible + 1);
  }
  const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  return (
    <div className="pm-pager">
      <span className="pm-pager-info">{start}–{end} of {total}</span>
      <div className="pm-pager-btns">
        <button className="pm-pager-btn" disabled={page <= 1} onClick={() => onPageChange(page - 1)}><IconChevLeft /></button>
        {pages.map(p => (
          <button key={p} className={`pm-pager-btn${p === page ? ' active' : ''}`} onClick={() => onPageChange(p)}>{p}</button>
        ))}
        <button className="pm-pager-btn" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}><IconChevRight /></button>
      </div>
    </div>
  );
};

// ─── Company List View ───
const CompanyListView = ({ companies, loading, pagination, search, onSearchChange, onPageChange, onSelect, onRefresh }) => (
  <div className="pm-panel">
    {/* Toolbar */}
    <div className="pm-toolbar">
      <div className="pm-search-box">
        <IconSearch />
        <input
          type="text"
          placeholder="Search company name or email…"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
        />
      </div>
      <button className="pm-icon-btn" onClick={onRefresh} title="Refresh">
        <IconRefresh />
        <span>Refresh</span>
      </button>
    </div>

    {/* Table */}
    {loading ? (
      <div className="pm-center">
        <div className="pm-spin" />
        <p>Loading…</p>
      </div>
    ) : companies.length === 0 ? (
      <div className="pm-center">
        <div className="pm-empty-icon">📊</div>
        <p className="pm-empty-title">No companies found</p>
        <p className="pm-empty-sub">Try adjusting your search</p>
      </div>
    ) : (
      <>
        <div className="pm-table-wrap">
          <table className="pm-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Status</th>
                <th>Plans</th>
                <th>Wallet Credits</th>
                <th>Total Spent</th>
                <th>Campaigns</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.companyid}>
                  <td>
                    <div className="pm-company-cell">
                      <div className="pm-avatar">{initials(c.companyname)}</div>
                      <div>
                        <div className="pm-company-name">{c.companyname}</div>
                        <div className="pm-company-email">{c.emailid}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className={c.status === 'Active' ? 'pm-pill green' : 'pm-pill red'}>{c.status}</span></td>
                  <td><span className="pm-num">{c.plans || 0}</span></td>
                  <td><span className="pm-num green">{fmt(c.wallet_credits || 0)}</span></td>
                  <td><span className="pm-num">{fmt(c.plan_total_spent || 0)}</span></td>
                  <td><span className="pm-num">{c.campaigns || 0}</span></td>
                  <td>
                    <button className="pm-view-btn" onClick={() => onSelect(c)}>
                      View <IconExternalLink />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pm-panel-footer">
          <PaginationBar pagination={pagination} onPageChange={onPageChange} />
        </div>
      </>
    )}
  </div>
);

// ─── Transaction Table ───
const TxnTable = ({ rows, cols, loading, pagination, onPageChange }) => {
  if (loading) return <div className="pm-center" style={{ minHeight: 180 }}><div className="pm-spin" /></div>;
  if (!rows || rows.length === 0) return (
    <div className="pm-center" style={{ minHeight: 160 }}>
      <div className="pm-empty-icon" style={{ fontSize: 32 }}>📭</div>
      <p className="pm-empty-sub">No transactions found</p>
    </div>
  );
  return (
    <>
      <div className="pm-table-wrap">
        <table className="pm-table">
          <thead><tr>{cols.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.transaction_id || r.transactionid || i}>
                {cols.map(c => <td key={c.key}>{c.render ? c.render(r) : r[c.key] ?? '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar pagination={pagination} onPageChange={onPageChange} />
    </>
  );
};

// ─── Company Detail View ───
const CompanyDetailView = ({ company, onBack }) => {
  const [activeTab, setActiveTab] = useState('payments');
  const [payTxns, setPayTxns] = useState([]);
  const [walTxns, setWalTxns] = useState([]);
  const [loadPay, setLoadPay] = useState(false);
  const [loadWal, setLoadWal] = useState(false);
  const [payPag, setPayPag] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
  const [walPag, setWalPag] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPay(1); fetchWal(1); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'payments') fetchPay(1); }, [activeTab]);

  const fetchPay = async (page = 1) => {
    setLoadPay(true);
    try {
      const res = await axiosConfig.post('/payment-management/payment-transactions', { companyid: company.companyid, page, pageSize: 10 });
      if (res.data?.status === true) {
        const txns = res.data.entity.payment_transactions || [];
        setPayTxns(txns);
        setPayPag({ page, pageSize: 10, total: txns.length, totalPages: Math.ceil(txns.length / 10) });
      }
    } catch { message.error('Failed to load payment transactions'); }
    finally { setLoadPay(false); }
  };

  const fetchWal = async (page = 1) => {
    setLoadWal(true);
    try {
      const res = await axiosConfig.post('/payment-management/wallet-transactions', { companyid: company.companyid, page, pageSize: 10 });
      if (res.data?.status === true) {
        const txns = res.data.entity.wallet_transactions || [];
        setWalTxns(txns);
        setWalPag({ page, pageSize: 10, total: txns.length, totalPages: Math.ceil(txns.length / 10) });
      }
    } catch { message.error('Failed to load wallet transactions'); }
    finally { setLoadWal(false); }
  };

  const payCols = [
    { key: 'transaction_id', label: 'Transaction ID', render: t => <span className="pm-mono">{t.transaction_id || '—'}</span> },
    { key: 'method', label: 'Method', render: t => <span className="pm-pill blue sm">{(t.method || 'N/A').toUpperCase()}</span> },
    { key: 'amount', label: 'Amount', render: t => <span className="pm-num">{fmt(t.amount)}</span> },
    { key: 'status', label: 'Status', render: t => <span className={statusCls(t.status)}>{t.status}</span> },
    { key: 'created_at', label: 'Date & Time', render: t => <span className="pm-date">{fmtDateTime(t.created_at)}</span> },
  ];

  const walCols = [
    { key: 'transactionid', label: 'Transaction ID', render: t => <span className="pm-mono">{t.transactionid || '—'}</span> },
    { key: 'transactiontype', label: 'Type', render: t => {
      const v = (t.transactiontype || '').toLowerCase();
      return <span className={`pm-pill sm ${v === 'credit' ? 'green' : 'red'}`}>{v.toUpperCase()}</span>;
    }},
    { key: 'messagecategory', label: 'Category', render: t => <span style={{ textTransform: 'capitalize' }}>{t.messagecategory || '—'}</span> },
    { key: 'amount', label: 'Amount', render: t => <span className="pm-num">{fmt(t.amount)}</span> },
    { key: 'balanceafter', label: 'Balance After', render: t => <span className="pm-num grey">{fmt(t.balanceafter)}</span> },
    { key: 'createdat', label: 'Date & Time', render: t => <span className="pm-date">{fmtDateTime(t.createdat)}</span> },
  ];

  return (
    <div className="pm-panel">
      {/* Breadcrumb Header */}
      <div className="pm-detail-head">
        <button className="pm-back-btn" onClick={onBack}>
          <IconArrowLeft /> Back
        </button>
        <div className="pm-detail-title">
          <div className="pm-avatar lg">{initials(company.companyname)}</div>
          <div>
            <h2>{company.companyname}</h2>
            <p>{company.emailid}</p>
          </div>
          <span className={company.status === 'Active' ? 'pm-pill green' : 'pm-pill red'}>{company.status}</span>
        </div>
      </div>

      {/* Metric Tiles */}
      <div className="pm-metrics">
        <div className="pm-metric">
          <span className="pm-metric-label">Total Plans</span>
          <span className="pm-metric-value">{company.plans || 0}</span>
        </div>
        <div className="pm-metric">
          <span className="pm-metric-label">Plan Spend</span>
          <span className="pm-metric-value">{fmt(company.plan_total_spent || 0)}</span>
        </div>
        <div className="pm-metric">
          <span className="pm-metric-label">Wallet Credits</span>
          <span className="pm-metric-value green">{fmt(company.wallet_credits || 0)}</span>
        </div>
        <div className="pm-metric">
          <span className="pm-metric-label">Wallet Debits</span>
          <span className="pm-metric-value red">{fmt(company.wallet_debits || 0)}</span>
        </div>
        <div className="pm-metric">
          <span className="pm-metric-label">Campaigns</span>
          <span className="pm-metric-value">{company.campaigns || 0}</span>
        </div>
        <div className="pm-metric">
          <span className="pm-metric-label">Transactions</span>
          <span className="pm-metric-value">{company.wallet_transactions || 0}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="pm-tabs">
        <button className={`pm-tab${activeTab === 'payments' ? ' active' : ''}`} onClick={() => setActiveTab('payments')}>Payment Transactions</button>
        <button className={`pm-tab${activeTab === 'wallet' ? ' active' : ''}`} onClick={() => setActiveTab('wallet')}>Wallet Transactions</button>
      </div>

      {/* Content */}
      <div className="pm-tab-body">
        {activeTab === 'payments'
          ? <TxnTable rows={payTxns} cols={payCols} loading={loadPay} pagination={payPag} onPageChange={fetchPay} />
          : <TxnTable rows={walTxns} cols={walCols} loading={loadWal} pagination={walPag} onPageChange={fetchWal} />}
      </div>
    </div>
  );
};

// ─── Failed Payments View ───
const FailedPaymentsView = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('all');
  const [companyId, setCompanyId] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const fetch = useCallback(async (pg = 1, st = status, cid = companyId) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (pg - 1) * PAGE_SIZE };
      if (st && st !== 'all') params.status = st;
      if (cid.trim()) params.company_id = cid.trim();
      const res = await axiosConfig.get('/admin/payments/failed', { params });
      if (res.data?.status === true) {
        const e = res.data.entity || res.data;
        const list = e.payments || e.data || e || [];
        setRows(Array.isArray(list) ? list : []);
        setTotal(e.total || (Array.isArray(list) ? list.length : 0));
        setPage(pg);
      } else {
        message.error(res.data?.message || 'Failed to load failed payments');
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Error loading failed payments');
    } finally {
      setLoading(false);
    }
  }, [status, companyId]);

  useEffect(() => { fetch(1, status, companyId); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = () => { fetch(1, status, companyId); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pagination = { page, totalPages, total, pageSize: PAGE_SIZE };

  const statusBadge = (s = '') => {
    const v = s.toLowerCase();
    if (v === 'failed') return <span className="pm-pill red sm">{s}</span>;
    if (v === 'authorized') return <span className="pm-pill blue sm">{s}</span>;
    if (v === 'created') return <span className="pm-pill amber sm">{s}</span>;
    return <span className="pm-pill grey sm">{s || '—'}</span>;
  };

  const cols = [
    { key: 'transaction_id', label: 'Transaction ID', render: r => <span className="pm-mono">{r.transaction_id || '—'}</span> },
    { key: 'razorpay_payment_id', label: 'Razorpay ID', render: r => <span className="pm-mono pm-muted">{r.razorpay_payment_id || '—'}</span> },
    { key: 'company_id', label: 'Company ID', render: r => <span className="pm-num grey">{r.company_id || r.companyid || '—'}</span> },
    { key: 'amount', label: 'Amount', render: r => <span className="pm-num">{r.amount != null ? fmt(r.amount) : '—'}</span> },
    { key: 'status', label: 'Status', render: r => statusBadge(r.status) },
    { key: 'created_at', label: 'Date & Time', render: r => <span className="pm-date">{fmtDateTime(r.created_at || r.createdat)}</span> },
  ];

  return (
    <div className="pm-panel pm-fp-panel">
      {/* Filter bar */}
      <div className="pm-toolbar pm-fp-toolbar">
        <span className="pm-filter-label">Status</span>
        <select
          className="pm-select"
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="failed">Failed</option>
          <option value="authorized">Authorized</option>
          <option value="created">Created</option>
        </select>
        <span className="pm-filter-label">Company ID</span>
        <input
          className="pm-filter-input"
          type="text"
          placeholder="Filter by company ID…"
          value={companyId}
          onChange={e => setCompanyId(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleFilter()}
        />
        <button className="pm-icon-btn pm-btn-primary" onClick={handleFilter}>
          <IconSearch /> Search
        </button>
        <button className="pm-icon-btn" onClick={() => { setStatus('all'); setCompanyId(''); fetch(1, 'all', ''); }} title="Clear filters">
          Clear
        </button>
        <div style={{ flex: 1 }} />
        <button className="pm-icon-btn" onClick={() => fetch(page, status, companyId)} title="Refresh">
          <IconRefresh /> Refresh
        </button>
      </div>

      {/* Result count */}
      {!loading && rows.length > 0 && (
        <div className="pm-result-bar">
          {total} payment{total !== 1 ? 's' : ''} found
          {status !== 'all' && <span className="pm-filter-tag">{status}</span>}
        </div>
      )}

      {/* Table */}
      <TxnTable rows={rows} cols={cols} loading={loading} pagination={pagination} onPageChange={p => fetch(p, status, companyId)} />
    </div>
  );
};

// ─── Reprocess Payment View ───
const ReprocessPaymentView = () => {
  const [txnId, setTxnId] = useState('');
  const [rzpId, setRzpId] = useState('');
  const [forceCredit, setForceCredit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null); // { success, message, data }

  const canSubmit = (txnId.trim() || rzpId.trim()) && !loading;

  const handleSubmit = () => {
    if (!txnId.trim() && !rzpId.trim()) {
      message.warning('Provide at least one of Transaction ID or Razorpay Payment ID.');
      return;
    }
    setConfirm(true);
  };

  const doReprocess = async () => {
    setConfirm(false);
    setLoading(true);
    setResult(null);
    try {
      const payload = { force_credit: forceCredit };
      if (txnId.trim()) payload.transaction_id = txnId.trim();
      if (rzpId.trim()) payload.razorpay_payment_id = rzpId.trim();
      const res = await axiosConfig.post('/admin/payments/reprocess', payload);
      if (res.data?.status === true) {
        setResult({ success: true, message: res.data.message || 'Payment reprocessed successfully.', data: res.data.entity || res.data.data });
        message.success('Payment reprocessed successfully');
        setTxnId(''); setRzpId(''); setForceCredit(false);
      } else {
        setResult({ success: false, message: res.data?.message || 'Reprocess failed.' });
        message.error(res.data?.message || 'Reprocess failed');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Error reprocessing payment';
      setResult({ success: false, message: msg });
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pm-action-grid">
      {/* Form card */}
      <div className="pm-panel pm-action-card">
        <div className="pm-action-card-head">
          <div className="pm-action-icon pm-action-icon--blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </div>
          <div>
            <h3 className="pm-action-title">Reprocess Failed Payment</h3>
            <p className="pm-action-sub">Retry a payment that failed due to a gateway or processing error. Only <strong>subscription plan</strong> payments can be reprocessed.</p>
          </div>
        </div>

        <div className="pm-warn-box" style={{ margin: '0 0 16px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span>Only subscription plan payments can be reprocessed. For quota corrections use <strong>Manual Credit</strong> instead.</span>
        </div>

        <div className="pm-form">
          <div className="pm-form-row">
            <label className="pm-label">Transaction ID <span className="pm-label-hint">(internal)</span></label>
            <input
              className="pm-input"
              type="text"
              placeholder="e.g. TXN-20240601-XXXX"
              value={txnId}
              onChange={e => setTxnId(e.target.value)}
            />
          </div>

          <div className="pm-form-or">
            <span>— OR —</span>
          </div>

          <div className="pm-form-row">
            <label className="pm-label">Razorpay Payment ID</label>
            <input
              className="pm-input"
              type="text"
              placeholder="e.g. pay_XXXXXXXXXXXXXXX"
              value={rzpId}
              onChange={e => setRzpId(e.target.value)}
            />
          </div>

          <div className="pm-form-row">
            <div className="pm-label pm-label--toggle">
              <span>Force Credit</span>
              <label className="pm-toggle">
                <input
                  type="checkbox"
                  checked={forceCredit}
                  onChange={e => setForceCredit(e.target.checked)}
                />
                <span className="pm-toggle-slider" />
              </label>
            </div>
            {forceCredit && (
              <div className="pm-warn-box">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>Force credit will apply wallet credits even if the payment gateway status is ambiguous. Use with caution.</span>
              </div>
            )}
          </div>

          <div className="pm-form-actions">
            <button
              className="pm-btn pm-btn--primary"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {loading ? <><span className="pm-spin-sm" /> Processing…</> : 'Reprocess Payment'}
            </button>
          </div>
        </div>
      </div>

      {/* Result / Info pane */}
      <div className="pm-side-info">
        {result ? (
          <div className={`pm-panel pm-result-card ${result.success ? 'success' : 'error'}`}>
            <div className="pm-result-icon">{result.success ? '✅' : '❌'}</div>
            <p className="pm-result-msg">{result.message}</p>
            {result.data && (
              <pre className="pm-result-json">{JSON.stringify(result.data, null, 2)}</pre>
            )}
          </div>
        ) : (
          <div className="pm-panel pm-info-hint">
            <h4>How it works</h4>
            <ul>
              <li>Provide either the internal <strong>Transaction ID</strong> or the <strong>Razorpay Payment ID</strong>.</li>
              <li>The system will verify the gateway status and credit the wallet if the payment was captured.</li>
              <li>Enable <strong>Force Credit</strong> only when you are certain the amount was deducted from the customer but credits were not applied.</li>
            </ul>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirm && (
        <div className="pm-modal-overlay" onClick={() => setConfirm(false)}>
          <div className="pm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="pm-modal-title">Confirm Reprocess</h3>
            <div className="pm-modal-body">
              <p>You are about to reprocess a payment:</p>
              <ul className="pm-modal-list">
                {txnId && <li><strong>Transaction ID:</strong> {txnId}</li>}
                {rzpId && <li><strong>Razorpay ID:</strong> {rzpId}</li>}
                <li><strong>Force Credit:</strong> {forceCredit ? 'Yes ⚠️' : 'No'}</li>
              </ul>
              {forceCredit && <div className="pm-warn-box">Force credit is enabled — credits will be applied regardless of gateway status.</div>}
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn pm-btn--ghost" onClick={() => setConfirm(false)}>Cancel</button>
              <button className="pm-btn pm-btn--primary" onClick={doReprocess}>Confirm Reprocess</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Manual Quota Grant View ───
const ManualCreditView = () => {
  const [companyId, setCompanyId] = useState('');
  const [waCampaigns, setWaCampaigns] = useState('');
  const [emailCampaigns, setEmailCampaigns] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);

  const canSubmit = companyId.trim() && (Number(waCampaigns) > 0 || Number(emailCampaigns) > 0) && reason.trim() && !loading;

  const handleSubmit = () => {
    if (!companyId.trim()) { message.warning('Company ID is required.'); return; }
    if (!Number(waCampaigns) && !Number(emailCampaigns)) { message.warning('Provide at least one quota value greater than 0.'); return; }
    if (!reason.trim()) { message.warning('Reason is required.'); return; }
    setConfirm(true);
  };

  const doCredit = async () => {
    setConfirm(false);
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        company_id: companyId.trim(),
        whatsapp_campaigns: Number(waCampaigns) || 0,
        email_campaigns: Number(emailCampaigns) || 0,
        reason: reason.trim(),
      };
      const res = await axiosConfig.post('/admin/payments/manual-credit', payload);
      if (res.data?.status === true) {
        setResult({ success: true, message: res.data.message || 'Quota granted successfully.', data: res.data.entity || res.data.data });
        message.success('Quota granted successfully');
        setCompanyId(''); setWaCampaigns(''); setEmailCampaigns(''); setReason('');
      } else {
        setResult({ success: false, message: res.data?.message || 'Manual quota grant failed.' });
        message.error(res.data?.message || 'Manual quota grant failed');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Error granting quota';
      setResult({ success: false, message: msg });
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pm-action-grid">
      {/* Form card */}
      <div className="pm-panel pm-action-card">
        <div className="pm-action-card-head">
          <div className="pm-action-icon pm-action-icon--green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>
          <div>
            <h3 className="pm-action-title">Manual Quota Grant</h3>
            <p className="pm-action-sub">Directly grant campaign quota to a company — use for compensations, adjustments, or plan corrections.</p>
          </div>
        </div>

        <div className="pm-form">
          <div className="pm-form-row">
            <label className="pm-label">Company ID <span className="pm-required">*</span></label>
            <input
              className="pm-input"
              type="text"
              placeholder="Enter the company ID…"
              value={companyId}
              onChange={e => setCompanyId(e.target.value)}
            />
          </div>

          <div className="pm-form-row">
            <label className="pm-label">WA Campaigns to Grant <span className="pm-label-hint">(0 to skip)</span></label>
            <input
              className="pm-input pm-input--half"
              type="number"
              min="0"
              placeholder="e.g. 10"
              value={waCampaigns}
              onChange={e => setWaCampaigns(e.target.value)}
            />
          </div>

          <div className="pm-form-row">
            <label className="pm-label">Emails to Grant <span className="pm-label-hint">(0 to skip)</span></label>
            <input
              className="pm-input pm-input--half"
              type="number"
              min="0"
              placeholder="e.g. 5000"
              value={emailCampaigns}
              onChange={e => setEmailCampaigns(e.target.value)}
            />
          </div>

          <div className="pm-form-row">
            <label className="pm-label">Reason <span className="pm-required">*</span></label>
            <textarea
              className="pm-textarea"
              rows={3}
              placeholder="Describe why quota is being manually granted…"
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </div>

          <div className="pm-form-actions">
            <button
              className="pm-btn pm-btn--success"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {loading ? <><span className="pm-spin-sm" /> Granting…</> : 'Grant Quota'}
            </button>
          </div>
        </div>
      </div>

      {/* Result / Info pane */}
      <div className="pm-side-info">
        {result ? (
          <div className={`pm-panel pm-result-card ${result.success ? 'success' : 'error'}`}>
            <div className="pm-result-icon">{result.success ? '✅' : '❌'}</div>
            <p className="pm-result-msg">{result.message}</p>
            {result.data && (
              <pre className="pm-result-json">{JSON.stringify(result.data, null, 2)}</pre>
            )}
          </div>
        ) : (
          <div className="pm-panel pm-info-hint">
            <h4>Guidelines</h4>
            <ul>
              <li>Always provide a clear <strong>reason</strong> — this is logged in the audit trail.</li>
              <li>Quota is added on top of any existing active-plan quota the company holds.</li>
              <li>Use <strong>WA Campaigns</strong> for WhatsApp broadcast slots and <strong>Emails</strong> for email send quota.</li>
            </ul>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirm && (
        <div className="pm-modal-overlay" onClick={() => setConfirm(false)}>
          <div className="pm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="pm-modal-title">Confirm Quota Grant</h3>
            <div className="pm-modal-body">
              <p>You are about to grant campaign quota:</p>
              <ul className="pm-modal-list">
                <li><strong>Company ID:</strong> {companyId}</li>
                {Number(waCampaigns) > 0 && <li><strong>WA Campaigns:</strong> {Number(waCampaigns).toLocaleString('en-IN')}</li>}
                {Number(emailCampaigns) > 0 && <li><strong>Emails:</strong> {Number(emailCampaigns).toLocaleString('en-IN')}</li>}
                <li><strong>Reason:</strong> {reason}</li>
              </ul>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn pm-btn--ghost" onClick={() => setConfirm(false)}>Cancel</button>
              <button className="pm-btn pm-btn--success" onClick={doCredit}>Confirm &amp; Grant</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───
const TOP_TABS = [
  { key: 'companies', label: 'Companies' },
  { key: 'failed', label: 'Failed Payments' },
  { key: 'reprocess', label: 'Reprocess Payment' },
  { key: 'manual-credit', label: 'Manual Quota' },
];

const PaymentManagement = () => {
  const [topTab, setTopTab] = useState('companies');

  // ── Companies state ──
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 1 });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (topTab === 'companies') fetchCompanies(1, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, topTab]);

  const fetchCompanies = useCallback(async (page = 1, searchText = '') => {
    setLoading(true);
    try {
      const res = await axiosConfig.post('/payment-management/all-companies', {
        page, pageSize: 10, search: searchText.trim(), excludeCompanyIds: []
      });
      if (res.data?.status === true && res.data?.statuscode === 200) {
        const e = res.data.entity;
        setCompanies(e.companies || []);
        setPagination({
          page: e.pagination?.page || page,
          pageSize: e.pagination?.pageSize || 20,
          total: e.pagination?.total || 0,
          totalPages: e.pagination?.totalPages || 1
        });
      } else {
        message.error(res.data?.message || 'Failed to fetch companies');
        setCompanies([]);
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to load payment data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTabChange = (key) => {
    setTopTab(key);
    if (key === 'companies') setSelectedCompany(null);
  };

  return (
    <div className="pm-root">
      {/* Top-level tabs */}
      <div className="pm-top-tabs">
        {TOP_TABS.map(t => (
          <button
            key={t.key}
            className={`pm-top-tab${topTab === t.key ? ' active' : ''}`}
            onClick={() => handleTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {topTab === 'companies' && (
        selectedCompany ? (
          <CompanyDetailView company={selectedCompany} onBack={() => setSelectedCompany(null)} />
        ) : (
          <CompanyListView
            companies={companies}
            loading={loading}
            pagination={pagination}
            search={search}
            onSearchChange={setSearch}
            onPageChange={p => fetchCompanies(p, debouncedSearch)}
            onSelect={setSelectedCompany}
            onRefresh={() => fetchCompanies(pagination.page, debouncedSearch)}
          />
        )
      )}

      {topTab === 'failed' && <FailedPaymentsView />}
      {topTab === 'reprocess' && <ReprocessPaymentView />}
      {topTab === 'manual-credit' && <ManualCreditView />}
    </div>
  );
};

export default PaymentManagement;
